import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, formatZAR, BACKEND_URL } from "@/lib/api";
import SiteFooter from "@/components/SiteFooter";
import CompletionForm from "@/components/CompletionForm";
import LegalRequirementsPortal from "@/components/LegalRequirementsPortal";
import ActionPlan from "@/components/ActionPlan";
import NextStepHero from "@/components/NextStepHero";
import TimelineImpact from "@/components/TimelineImpact";
import CompletenessGauge from "@/components/CompletenessGauge";
import ValidityPanel from "@/components/ValidityPanel";
import EstateOverviewPanel from "@/components/EstateOverviewPanel";
import {
  Scale, CheckCircle2, Clock, Ban, Loader2, Sparkles,
  ShieldCheck, MessageSquare, ArrowRight, AlertTriangle, Flame, Hourglass,
  Upload as UploadIcon, Check,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  new_lead: "Matter opened",
  awaiting_documents: "Awaiting your documents",
  in_progress: "In progress with conveyancer",
  lodged: "Lodged at Deeds Office",
  completed: "Registration complete",
};

const TASK_STATUS_META = {
  pending: { icon: Clock, color: "text-zinc-400", bg: "bg-white", label: "Pending" },
  in_progress: { icon: Loader2, color: "text-[#0055FF]", bg: "bg-blue-50", label: "In progress" },
  done: { icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50", label: "Complete" },
  blocked: { icon: Ban, color: "text-red-600", bg: "bg-red-50", label: "Blocked" },
};

const RISK_META = {
  low: { bg: "bg-green-100", text: "text-green-800" },
  medium: { bg: "bg-amber-100", text: "text-amber-800" },
  high: { bg: "bg-red-100", text: "text-red-800" },
};

const PRIORITY_META = {
  urgent: { label: "Urgent", icon: Flame, cls: "bg-red-600 text-white" },
  high:   { label: "High",   icon: Flame, cls: "bg-amber-600 text-white" },
  medium: { label: "Medium", icon: Clock, cls: "bg-zinc-200 text-zinc-800" },
  low:    { label: "Low",    icon: CheckCircle2, cls: "bg-zinc-100 text-zinc-600" },
};


export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const prevStepStatusRef = useRef({});

  const refresh = () => {
    api
      .get(`/client/portal/${token}`)
      .then((r) => {
        // Detect newly completed CLIENT steps to fire the
        // "Step complete — moving to next stage" toast.
        const newPlan = r.data?.action_plan;
        if (newPlan?.steps && Object.keys(prevStepStatusRef.current).length) {
          const newlyComplete = newPlan.steps.filter(
            (s) =>
              s.action_kind !== "milestone" &&
              s.status === "complete" &&
              prevStepStatusRef.current[s.id] &&
              prevStepStatusRef.current[s.id] !== "complete",
          );
          newlyComplete.forEach((s) => {
            toast.success(`Step complete — moving to next stage`, {
              description: s.title,
            });
          });
        }
        if (newPlan?.steps) {
          prevStepStatusRef.current = Object.fromEntries(
            newPlan.steps.map((s) => [s.id, s.status]),
          );
        }
        setData(r.data);
      })
      .catch((e) => setError(formatApiError(e)));
  };

  useEffect(() => { refresh(); }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-background grain p-6">
        <div className="text-center max-w-md">
          <Ban className="h-10 w-10 text-zinc-300 mx-auto" />
          <h1 className="mt-4 font-serif text-3xl">Portal not available</h1>
          <p className="mt-2 text-sm text-zinc-500">{error}</p>
          <Link to="/" className="mt-6 inline-block text-sm text-zinc-700 underline">← Back to home</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen grid place-items-center text-zinc-500">Loading your matter…</div>;
  }

  const { matter, tasks, handled_by_firm, legal_insight_summary, notifications, time_remaining, delay_alerts, focus, pipeline_step, accountability, proforma, invoices = [], billing_summary, action_plan, validity, decisions, completeness } = data;
  const firmTasks = handled_by_firm || [];
  const portalToken = token;
  const done = focus?.completed_count ?? tasks.filter((t) => t.status === "done").length;
  const progressPct = tasks.length === 0 ? 0 : Math.round((done / tasks.length) * 100);
  const blocked = matter.blocked_count ?? tasks.filter((t) => t.status === "blocked").length;
  const riskMeta = RISK_META[matter.risk_profile] || RISK_META.medium;

  const filtered =
    filter === "all"
      ? tasks
      : tasks.filter((t) => (filter === "blockers" ? t.blocks_registration : t.category === filter));
  // Sort by priority_score desc, then order asc
  const sortedFiltered = [...filtered].sort(
    (a, b) => (b.priority_score || 0) - (a.priority_score || 0) || (a.order || 0) - (b.order || 0),
  );

  return (
    <div className="min-h-screen bg-background grain" data-testid="client-portal">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-5xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 bg-zinc-900 text-white grid place-items-center">
              <Scale className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-serif text-lg font-semibold leading-none">EH Inc</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">Client portal</div>
            </div>
          </Link>
          <span className="text-xs text-zinc-500">Secure link · do not share</span>
        </div>
      </header>

      {/* PHASE 5.2 — Behaviour-change anchor */}
      <div className="bg-[#0055FF] text-white" data-testid="portal-anchor">
        <div className="mx-auto max-w-5xl px-6 py-3 text-sm flex flex-wrap items-center gap-3 justify-between">
          <span>
            <span className="font-semibold">This is your live transfer workspace.</span>{" "}
            All updates and actions will happen here.
          </span>
          <Link to="/find-my-matter" className="text-xs underline opacity-90 hover:opacity-100">
            Lost the link? Recover →
          </Link>
        </div>
      </div>

      {/* P0 — Next step hero: always-visible "Do this next" banner */}
      <NextStepHero
        plan={action_plan}
        onClick={(step) => {
          const el = document.querySelector(`[data-testid="${step.anchor}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />

      {/* Phase 10.1 — Estate Overview (only renders for deceased_estate
          matters created from the public calculator) */}
      <div className="mx-auto max-w-5xl px-6 mt-6">
        <EstateOverviewPanel matter={matter} role="client" />
      </div>

      {/* Header card */}
      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-5xl px-6 py-12 grid lg:grid-cols-3 gap-10 items-end">
          <div className="lg:col-span-2">
            <div className="text-overline text-zinc-400">Your matter</div>
            <h1 className="mt-3 font-serif text-4xl lg:text-5xl font-medium tracking-tight">
              Hello, {matter.client_name?.split(" ")[0] || "there"}.
            </h1>
            <p className="mt-3 text-zinc-300 text-sm leading-relaxed max-w-xl">
              {STATUS_LABELS[matter.status] || matter.status}. {legal_insight_summary?.timeline_total
                ? `Typical timeline: ${legal_insight_summary.timeline_total}.`
                : ""}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
              <span className={`px-2 py-1 tracking-widest uppercase ${riskMeta.bg} ${riskMeta.text}`}>
                Risk: {matter.risk_profile}
              </span>
              <span className="px-2 py-1 tracking-widest uppercase bg-zinc-800 text-zinc-300">
                {matter.profile?.property_type}
              </span>
              <span className="px-2 py-1 tracking-widest uppercase bg-zinc-800 text-zinc-300">
                {matter.profile?.has_bond ? "Bond" : "Cash"}
              </span>
              <span className="px-2 py-1 tracking-widest uppercase bg-zinc-800 text-zinc-300">
                {matter.profile?.buyer_type}
              </span>
              {matter.value > 0 && (
                <span className="px-2 py-1 tracking-widest uppercase bg-zinc-800 text-zinc-300">
                  {formatZAR(matter.value)}
                </span>
              )}
            </div>
          </div>

          {/* Progress widget */}
          <div className="border border-zinc-700 p-6">
            <div className="flex items-center justify-between text-overline text-zinc-400">
              <span>Progress</span>
              <span data-testid="progress-pct">{progressPct}%</span>
            </div>
            <div className="mt-3 h-1.5 bg-zinc-800 relative">
              <div
                className="absolute inset-y-0 left-0 bg-[#0055FF]"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="mt-4 text-xs text-zinc-400 flex justify-between">
              <span>{done} of {tasks.length} complete</span>
              {blocked > 0 && <span className="text-red-400">{blocked} blocked</span>}
            </div>
          </div>
        </div>
      </section>

      {/* Pipeline strip */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-5xl px-6 py-6 grid grid-cols-5 gap-2 text-center">
          {Object.entries(STATUS_LABELS).map(([k, label], i) => {
            const active = matter.status === k;
            const passed = ["new_lead", "awaiting_documents", "in_progress", "lodged", "completed"].indexOf(matter.status) >= i;
            return (
              <div key={k} className={`py-2 text-xs ${active ? "border-b-2 border-zinc-900 font-semibold" : passed ? "border-b border-zinc-200 text-zinc-700" : "border-b border-zinc-100 text-zinc-400"}`}>
                <div className="font-mono text-[10px]">0{i + 1}</div>
                <div className="mt-1 leading-tight">{label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Time remaining + step indicator */}
      <section className="bg-white border-b border-zinc-200">
        <div className="mx-auto max-w-5xl px-6 py-6 grid sm:grid-cols-3 gap-4">
          <Metric
            label="You are here"
            value={`Step ${pipeline_step?.step_number ?? 1} of ${pipeline_step?.total_steps ?? 5}`}
            sub={pipeline_step?.label}
            testid="metric-step"
          />
          <Metric
            label="Estimated time remaining"
            value={time_remaining?.display ?? "—"}
            sub={`${time_remaining?.days_min ?? 0}–${time_remaining?.days_max ?? 0} calendar days (combined)`}
            testid="metric-time-remaining"
          />
          <Metric
            label="Matter priority"
            value={matter.priority_level || "—"}
            sub={`Score ${matter.priority_score ?? 0} · ${matter.pending_blockers ?? 0} pending blockers`}
            testid="metric-priority"
            accent={matter.priority_level === "urgent" || matter.priority_level === "high" ? "warn" : null}
          />
        </div>
      </section>

      {/* P0 — Action Plan (guided journey) — prominently above the fold */}
      <section className="mx-auto max-w-5xl px-6 pt-12" data-testid="action-plan-section">
        <CompletenessGauge completeness={completeness} />
        <div className="mt-4">
          <TimelineImpact plan={action_plan} accountability={accountability} />
        </div>
        <div className="mt-4">
          <ActionPlan plan={action_plan} />
        </div>
        <div className="mt-6">
          <ValidityPanel validity={validity} decisions={decisions} />
        </div>
      </section>

      {/* PHASE 5 — Accountability hero: Expected date + Confidence */}
      {accountability && (
        <section className="bg-zinc-900 text-white border-b border-zinc-700" data-testid="accountability-hero">
          <div className="mx-auto max-w-5xl px-6 py-12 grid lg:grid-cols-2 gap-10 items-end">
            <div>
              <div className="flex items-center gap-2 text-overline text-zinc-400">
                <Sparkles className="h-3 w-3" /> Expected registration date
              </div>
              <div className="mt-3 font-serif text-4xl lg:text-5xl font-medium tracking-tighter leading-none" data-testid="expected-completion-date">
                {accountability.expected_completion?.display}
              </div>
              <p className="mt-3 text-sm text-zinc-400">
                Optimistic: {accountability.expected_completion?.optimistic_display} ·{" "}
                Pessimistic: {accountability.expected_completion?.pessimistic_display}
              </p>
            </div>
            <ConfidenceWidget conf={accountability.confidence} />
          </div>
        </section>
      )}

      {/* PHASE 5 — Intervention signal (red banner) */}
      {accountability?.intervention?.required && (
        <section className="bg-red-700 text-white border-b border-red-800" data-testid="intervention-banner">
          <div className="mx-auto max-w-5xl px-6 py-6">
            <div className="flex items-center gap-2 text-overline text-red-200">
              <AlertTriangle className="h-3 w-3" /> Active intervention
            </div>
            <h2 className="mt-2 font-serif text-2xl">This matter requires intervention</h2>
            <ul className="mt-4 space-y-2">
              {accountability.intervention.items.map((i, k) => (
                <li key={k} className="text-sm flex items-start gap-3">
                  <span className="mt-0.5">→</span>
                  <div>
                    <div className="font-medium">{i.title} <span className="opacity-70 text-xs">— {i.reason}</span></div>
                    <div className="text-xs text-red-200 mt-0.5">Action needed: {i.recommended_action}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Delay alerts banner (read-only summary; intervention banner above takes priority) */}
      {delay_alerts && delay_alerts.length > 0 && !accountability?.intervention?.required && (
        <section className="bg-red-50 border-b border-red-200" data-testid="delay-alerts">
          <div className="mx-auto max-w-5xl px-6 py-6">
            <div className="flex items-center gap-2 text-overline text-red-700">
              <AlertTriangle className="h-3 w-3" /> Your transfer may be delayed
            </div>
            <ul className="mt-3 space-y-1.5">
              {delay_alerts.map((a, i) => (
                <li key={i} className="text-sm text-red-900 flex items-start gap-2">
                  <span className="text-red-600 mt-0.5">⚠</span>
                  <span><span className="font-medium">{a.title}</span> — <span className="text-red-700">{a.reason}</span></span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* PHASE 5 — Where you might lose time */}
      {accountability?.likely_delays?.length > 0 && (
        <section className="bg-white border-b border-zinc-200" data-testid="likely-delays">
          <div className="mx-auto max-w-5xl px-6 py-12">
            <div className="text-overline text-zinc-500 flex items-center gap-2">
              <Hourglass className="h-3 w-3" /> Where you might lose time
            </div>
            <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight">Most likely delay points</h2>
            <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
              Based on your matter profile (property type, buyer, bond) and any active alerts.
            </p>
            <ul className="mt-6 grid md:grid-cols-2 gap-3">
              {accountability.likely_delays.map((d, i) => {
                const lvlCls =
                  d.level === "high" ? "bg-red-50 border-red-200" :
                  d.level === "medium" ? "bg-amber-50 border-amber-200" :
                  "bg-zinc-50 border-zinc-200";
                return (
                  <li key={i} className={`border p-4 ${lvlCls}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{d.title}</span>
                      <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 ${
                        d.level === "high" ? "bg-red-100 text-red-700" :
                        d.level === "medium" ? "bg-amber-100 text-amber-800" :
                        "bg-zinc-100 text-zinc-700"
                      }`}>{d.level}</span>
                      {d.active && (
                        <span className="text-[10px] tracking-widest uppercase bg-red-600 text-white px-1.5 py-0.5">active</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-zinc-600 leading-relaxed">{d.reason}</p>
                    {d.source && <p className="mt-1 text-[10px] font-mono italic text-zinc-500">Source: {d.source}</p>}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {/* PHASE 5 — Transparency Commitment */}
      {accountability?.commitment && (
        <section className="bg-zinc-50 border-b border-zinc-200" data-testid="transparency-commitment">
          <div className="mx-auto max-w-5xl px-6 py-10 grid lg:grid-cols-3 gap-6 items-start">
            <div>
              <div className="text-overline text-zinc-500">EH Inc</div>
              <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight">{accountability.commitment.headline}</h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-sm text-zinc-700 leading-relaxed">
                {accountability.commitment.body}
              </p>
              <div className="mt-4 inline-flex items-center gap-3 bg-zinc-900 text-white px-4 py-3">
                <ShieldCheck className="h-4 w-4 text-[#0055FF]" />
                <span className="text-sm">
                  We expect your transfer to complete by{" "}
                  <span className="font-semibold">{accountability.expected_completion?.pessimistic_display}</span>
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* What to prioritise now */}
      <section className="bg-zinc-50 border-b border-zinc-200" data-testid="priority-focus">
        <div className="mx-auto max-w-5xl px-6 py-12">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
            <div>
              <div className="text-overline text-zinc-500 flex items-center gap-2">
                <Flame className="h-3 w-3 text-red-600" /> What to prioritise now
              </div>
              <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight">Your top focus today</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <FocusColumn
              title="Do this now"
              icon={<Flame className="h-4 w-4 text-red-600" />}
              empty="Nothing urgent — well done."
              tone="urgent"
              tasks={focus?.do_now || []}
              testid="focus-do-now"
            />
            <FocusColumn
              title="Waiting on"
              icon={<Hourglass className="h-4 w-4 text-amber-600" />}
              empty="Nothing blocked."
              tone="waiting"
              tasks={focus?.waiting_on || []}
              testid="focus-waiting"
            />
            <FocusColumn
              title="Completed"
              icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
              empty="Tasks will appear here as they're marked done."
              tone="done"
              tasks={focus?.completed || []}
              testid="focus-completed"
              compact
            />
          </div>
        </div>
      </section>

      {/* Pro-forma artefact — Phase 5.3 (revised) */}
      {proforma && (
        <ProformaCard
          proforma={proforma}
          matterPricingMode={matter.pricing_mode}
          portalToken={portalToken}
          onAccepted={refresh}
        />
      )}

      {/* Phase 7 — Billing section (issued + paid invoices) */}
      {invoices.length > 0 && (
        <BillingCard
          invoices={invoices}
          summary={billing_summary}
          portalToken={portalToken}
          onPaid={refresh}
        />
      )}

      {/* Phase 5.4 — Offer received banner */}
      {matter.otp_received_at && (
        <section className="mx-auto max-w-5xl px-6 pt-8" data-testid="otp-received-banner">
          <div className="bg-green-50 border border-green-200 px-6 py-4 flex items-center gap-4">
            <div className="h-9 w-9 bg-green-600 text-white grid place-items-center rounded-full shrink-0">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-overline text-green-800">Step 1 complete</div>
              <div className="font-medium text-green-900">Offer received</div>
              <div className="text-xs text-green-700 mt-1">
                Filed {new Date(matter.otp_received_at).toLocaleDateString()} ·
                Extraction: <span className="font-mono">{matter.otp_extraction_status || "pending"}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* P0 — Legal Compliance: FICA + service-specific requirements */}
      <section className="mx-auto max-w-5xl px-6 pt-12">
        <LegalRequirementsPortal token={token} onChanged={refresh} />
      </section>

      {/* Phase UX-6.1 — Structured data completion */}
      <section className="mx-auto max-w-5xl px-6 pt-12">
        <CompletionForm token={token} />
      </section>

      {/* Action plan */}
      <section className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-overline text-zinc-500 flex items-center gap-2">
              <Sparkles className="h-3 w-3" /> Your action plan
            </div>
            <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight">What's required to complete your transfer</h2>
          </div>
          <div className="flex border border-zinc-200 text-xs">
            {[
              { v: "all", l: `All (${tasks.length})` },
              { v: "action", l: "Actions" },
              { v: "requirement", l: "Requirements" },
              { v: "blockers", l: "Blockers" },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`px-3 py-2 ${filter === f.v ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                data-testid={`filter-${f.v}`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>

        <ol className="space-y-3" data-testid="task-list">
          {sortedFiltered.map((t, i) => {
            const meta = TASK_STATUS_META[t.status] || TASK_STATUS_META.pending;
            const Icon = meta.icon;
            const pMeta = PRIORITY_META[t.priority_level] || PRIORITY_META.medium;
            const PIcon = pMeta.icon;
            const hasClientAction = t.client_action_type && t.client_action_type !== "none" && t.status !== "done";
            return (
              <li
                key={t.key}
                className={`border border-zinc-200 ${meta.bg} p-5 grid grid-cols-12 gap-4 items-start`}
                data-testid={`task-${t.key}`}
              >
                <div className="col-span-1 font-mono text-xs text-zinc-400 pt-1">{String(i + 1).padStart(2, "0")}</div>
                <div className="col-span-10">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{t.title}</span>
                    <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 inline-flex items-center gap-1 ${pMeta.cls}`}>
                      <PIcon className="h-2.5 w-2.5" /> {pMeta.label}
                    </span>
                    {t.blocks_registration && (
                      <span className="text-[10px] tracking-widest uppercase bg-red-100 text-red-700 px-1.5 py-0.5">blocker</span>
                    )}
                    <span className="text-[10px] tracking-widest uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5">
                      {t.category}
                    </span>
                    {(t.estimated_days_min || t.estimated_days_max) && (
                      <span className="text-[10px] tracking-widest uppercase bg-zinc-50 text-zinc-500 px-1.5 py-0.5 font-mono">
                        {t.estimated_days_min}–{t.estimated_days_max}d
                      </span>
                    )}
                    {hasClientAction && (
                      <span className="text-[10px] tracking-widest uppercase bg-[#0055FF] text-white px-1.5 py-0.5">
                        your action
                      </span>
                    )}
                  </div>
                  {t.detail && <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{t.detail}</p>}
                  {hasClientAction && (
                    <ClientActionRow
                      portalToken={portalToken}
                      task={t}
                      onComplete={refresh}
                    />
                  )}
                  {t.uploaded_file && (
                    <div className="mt-2 text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 px-3 py-2 flex items-center gap-2">
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                      Uploaded: <span className="font-medium">{t.uploaded_file.name}</span>
                      <span className="text-zinc-400">({Math.round(t.uploaded_file.size / 1024)} KB)</span>
                    </div>
                  )}
                  {t.source && (
                    <p className="mt-2 text-[10px] font-mono italic text-zinc-500">Source: {t.source}</p>
                  )}
                </div>
                <div className="col-span-1 flex justify-end pt-1">
                  <Icon className={`h-5 w-5 ${meta.color} ${t.status === "in_progress" ? "animate-spin" : ""}`} />
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      {/* Updates / notifications */}
      <section className="border-t border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-5xl px-6 py-12">
          <div className="text-overline text-zinc-500 flex items-center gap-2">
            <MessageSquare className="h-3 w-3" /> Updates from your conveyancer
          </div>
          <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight">Activity feed</h2>
          <div className="mt-6 space-y-3">
            {notifications.length === 0 ? (
              <p className="text-sm text-zinc-500">No updates yet. We'll post here as your matter progresses.</p>
            ) : (
              notifications.map((n) => (
                <div key={n.id} className="bg-white border border-zinc-200 p-5">
                  <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
                    <span className="px-1.5 py-0.5 bg-zinc-100">{n.channel}</span>
                    <span>{n.trigger}</span>
                    <span className="ml-auto text-zinc-400 normal-case tracking-normal">
                      {new Date(n.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-700">{n.content}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Handled by EH Inc — non-client tasks (transparency) */}
      {firmTasks.length > 0 && (
        <section className="mx-auto max-w-5xl px-6 py-12" data-testid="handled-by-firm">
          <div className="text-overline text-zinc-500 mb-3 flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> Handled by EH Inc
          </div>
          <h2 className="font-serif text-2xl font-medium tracking-tight mb-4">
            What we're doing for you in the background
          </h2>
          <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
            {firmTasks.map((t) => (
              <div key={t.key} className="px-5 py-3 flex items-center justify-between gap-4" data-testid={`firm-task-${t.key}`}>
                <div className="min-w-0">
                  <div className="text-sm truncate">{t.title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    Owner: {(t.owner || "back_office").replace("_", " ")} · {t.category}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 ${
                  t.status === "done" ? "bg-green-50 text-green-700" :
                  t.status === "in_progress" ? "bg-blue-50 text-blue-700" :
                  t.status === "blocked" ? "bg-red-50 text-red-700" : "bg-zinc-100 text-zinc-600"
                }`}>
                  {t.status.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-3">
            These steps don&apos;t need anything from you — we&apos;ll keep you posted as each one completes.
          </p>
        </section>
      )}

      {/* CTA — continue intake */}
      <section className="border-t border-zinc-200 bg-white">
        <div className="mx-auto max-w-5xl px-6 py-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-overline text-zinc-500">Help us go faster</div>
            <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight">Continue your intake</h2>
            <p className="mt-3 text-zinc-600 text-sm leading-relaxed">
              Add the remaining details (full names, ID numbers, OTP info) so your conveyancer can prepare the transfer documents — our AI has already skipped the questions you answered in the calculator.
            </p>
            <Link
              to={`/intake?pre=${matter.id}`}
              className="mt-6 inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-3 text-sm font-medium hover:bg-zinc-800 transition-colors"
              data-testid="continue-intake-cta"
            >
              Continue intake <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 p-8">
            <ShieldCheck className="h-6 w-6 text-zinc-400" />
            <p className="mt-4 text-sm text-zinc-600 leading-relaxed">
              Your portal link is private. Anyone with this URL can view (but not edit) your matter. Bookmark it — we'll keep updating it as your transfer progresses.
            </p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}


function Metric({ label, value, sub, accent, testid }) {
  const accentCls = accent === "warn" ? "bg-amber-50 border-amber-200" : "bg-white border-zinc-200";
  return (
    <div className={`p-5 border ${accentCls}`} data-testid={testid}>
      <div className="text-overline text-zinc-500">{label}</div>
      <div className="mt-2 font-serif text-2xl font-medium tracking-tight capitalize">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

function FocusColumn({ title, icon, tasks, empty, tone, testid, compact }) {
  const headerBg =
    tone === "urgent" ? "bg-red-50 border-red-200" :
    tone === "waiting" ? "bg-amber-50 border-amber-200" :
    "bg-green-50 border-green-200";
  return (
    <div className="border border-zinc-200 bg-white" data-testid={testid}>
      <div className={`px-4 py-3 border-b ${headerBg} flex items-center justify-between`}>
        <div className="flex items-center gap-2 font-medium text-sm">
          {icon} {title}
        </div>
        <span className="text-xs text-zinc-500">{tasks.length}</span>
      </div>
      <div className="p-2 max-h-[280px] overflow-y-auto">
        {tasks.length === 0 ? (
          <div className="p-4 text-xs text-zinc-500 text-center">{empty}</div>
        ) : (
          <ul className="space-y-1">
            {tasks.map((t) => (
              <li key={t.key} className="px-3 py-2 hover:bg-zinc-50 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={compact ? "line-through text-zinc-400" : ""}>{t.title}</span>
                  {!compact && t.estimated_days_min && (
                    <span className="text-[10px] font-mono text-zinc-400">
                      ~{t.estimated_days_min}–{t.estimated_days_max}d
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function ConfidenceWidget({ conf }) {
  if (!conf) return null;
  const level = conf.level || "medium";
  const score = conf.score ?? 0;
  const ringColor =
    level === "high" ? "#16A34A" :
    level === "medium" ? "#D97706" :
    "#DC2626";
  // SVG ring (circumference 2πr, r=42 → ~263)
  const radius = 42;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="border border-zinc-700 p-6 grid grid-cols-[auto,1fr] gap-6 items-center" data-testid="confidence-widget">
      <div className="relative h-[110px] w-[110px]">
        <svg width="110" height="110" viewBox="0 0 110 110" className="absolute inset-0">
          <circle cx="55" cy="55" r={radius} stroke="#27272A" strokeWidth="8" fill="none" />
          <circle
            cx="55" cy="55" r={radius} stroke={ringColor} strokeWidth="8" fill="none"
            strokeDasharray={circ} strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 55 55)"
          />
        </svg>
        <div
          className="absolute inset-0 grid place-items-center font-serif text-2xl text-white"
          data-testid="confidence-score"
        >
          {score}%
        </div>
      </div>
      <div>
        <div className="text-overline text-zinc-400">Confidence</div>
        <div className="font-serif text-3xl font-medium tracking-tight mt-1 capitalize" data-testid="confidence-level">{level}</div>
        {conf.factors && conf.factors.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-zinc-400">
            {conf.factors.map((f, i) => (
              <li key={i}>
                <span className="font-mono text-red-400 mr-2">{f.delta}%</span>
                {f.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ClientActionRow({ portalToken, task, onComplete }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/client/portal/${portalToken}/tasks/${task.key}/complete`, {});
      toast.success("Marked complete — your matter has been updated.");
      onComplete?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8 MB per file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${BACKEND_URL}/api/client/portal/${portalToken}/tasks/${task.key}/upload`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      toast.success(`Uploaded ${file.name}`);
      onComplete?.();
    } catch (e) {
      toast.error("Upload failed — please try again");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-3 bg-[#0055FF]/5 border border-[#0055FF]/30 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`client-action-${task.key}`}>
      <div className="text-sm text-zinc-700">
        <span className="font-medium">{task.client_action_label || "Action required"}</span>
      </div>
      {task.upload_required ? (
        <>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={handleFile}
            data-testid={`upload-input-${task.key}`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="bg-[#0055FF] hover:bg-[#003ECC] text-white text-xs px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid={`upload-btn-${task.key}`}
          >
            <UploadIcon className="h-3 w-3" /> {busy ? "Uploading…" : "Upload file"}
          </button>
        </>
      ) : (
        <button
          onClick={confirm}
          disabled={busy}
          className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid={`confirm-btn-${task.key}`}
        >
          <Check className="h-3 w-3" /> {busy ? "Saving…" : "I've done this"}
        </button>
      )}
    </div>
  );
}



// Phase 5.3 (revised) — pro-forma artefact inside the portal
function ProformaCard({ proforma, matterPricingMode, portalToken, onAccepted }) {
  const [mode, setMode] = useState(proforma.pricing_mode || matterPricingMode || "managed");
  const [busy, setBusy] = useState(false);
  const accepted = proforma.status === "accepted";
  const pricing  = proforma.pricing || {};

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/client/portal/${portalToken}/proforma/accept`, { pricing_mode: mode });
      toast.success(`Pro-forma accepted (${mode}).`);
      onAccepted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-5xl px-6 pt-12" data-testid="portal-proforma">
      <div className="border border-zinc-900 bg-white">
        <div className="px-8 py-6 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-overline text-zinc-500">Pro-forma</div>
            <h2 className="font-serif text-3xl font-medium tracking-tight mt-1">
              Your formal estimate
            </h2>
            <p className="text-sm text-zinc-600 mt-1">
              Issued {new Date(proforma.created_at).toLocaleDateString()} · expires{" "}
              {proforma.expires_at ? new Date(proforma.expires_at).toLocaleDateString() : "—"} · #{proforma.id.slice(0, 8)}
            </p>
          </div>
          <span
            className={`text-[10px] uppercase tracking-widest px-3 py-1.5 ${
              accepted ? "bg-green-50 text-green-800" : "bg-blue-50 text-blue-800"
            }`}
            data-testid="proforma-status-pill"
          >
            {accepted ? `Accepted · ${matterPricingMode}` : "Awaiting acceptance"}
          </span>
        </div>

        <div className="grid md:grid-cols-2">
          {["managed", "assisted"].map((k) => {
            const p = pricing[k];
            if (!p) return null;
            const isActive = mode === k;
            return (
              <button
                key={k}
                onClick={() => !accepted && setMode(k)}
                disabled={accepted}
                className={`text-left p-6 border-r last:border-r-0 border-zinc-100 transition-colors ${
                  isActive ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"
                } ${accepted ? "cursor-default" : "cursor-pointer"}`}
                data-testid={`portal-proforma-mode-${k}`}
              >
                <div className={`text-overline ${isActive ? "text-zinc-400" : "text-zinc-500"}`}>{k}</div>
                <div className="font-serif text-xl mt-2">{p.label}</div>
                <div className={`mt-2 text-xs ${isActive ? "text-zinc-300" : "text-zinc-600"}`}>{p.description}</div>
                <div className="mt-4 font-serif text-2xl tracking-tight">
                  {formatZAR(p.min)} <span className={isActive ? "text-zinc-400 text-sm" : "text-zinc-500 text-sm"}>— {formatZAR(p.max)}</span>
                </div>
                <div className={`mt-1 text-[11px] font-mono ${isActive ? "text-zinc-400" : "text-zinc-500"}`}>
                  Mid: {formatZAR(p.mid)} · ZAR
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-8 py-5 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-zinc-600 max-w-md leading-relaxed">
            {accepted
              ? "Thanks — your pro-forma has been accepted. Your pricing is now locked in."
              : <>SARS transfer duty and Deeds Office fees are exact statutory lookups. The legal/admin portion shows a ±{pricing.spread_pct}% range. Pick your mode and accept to lock it in.</>
            }
          </p>
          <div className="flex items-center gap-2">
            <Link
              to={`/proforma/${proforma.id}`}
              className="text-xs underline text-zinc-700 hover:text-zinc-900"
              data-testid="proforma-view-shareable"
            >
              View shareable copy
            </Link>
            {!accepted && (
              <button
                onClick={accept}
                disabled={busy}
                className="bg-[#0055FF] hover:bg-[#003ECC] text-white text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="portal-proforma-accept"
              >
                <Check className="h-4 w-4" /> {busy ? "Saving…" : `Accept ${mode}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}



// Phase 7 — Billing card (client portal)
function BillingCard({ invoices, summary, portalToken, onPaid }) {
  const [payingId, setPayingId] = useState(null);

  const pay = async (invId) => {
    setPayingId(invId);
    try {
      await api.post(`/client/portal/${portalToken}/invoices/${invId}/pay`);
      toast.success("Payment confirmed");
      onPaid?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPayingId(null);
    }
  };

  const outstanding = summary?.outstanding_total ?? 0;
  const paid = summary?.paid_total ?? 0;
  const issuedInvoices = invoices.filter((i) => i.status === "issued");
  const paidInvoices = invoices.filter((i) => i.status === "paid");

  return (
    <section className="mx-auto max-w-5xl px-6 pt-12" data-testid="portal-billing">
      <div className="bg-white border border-zinc-200 p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-zinc-500">Phase 7 · Billing</div>
            <h2 className="font-serif text-3xl mt-1">Your invoices</h2>
          </div>
          <div className="flex gap-6 text-sm">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500">Outstanding</div>
              <div className="font-serif text-2xl text-zinc-900" data-testid="portal-billing-outstanding">{formatZAR(outstanding)}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500">Paid</div>
              <div className="font-serif text-2xl text-green-700" data-testid="portal-billing-paid">{formatZAR(paid)}</div>
            </div>
          </div>
        </div>

        {issuedInvoices.length > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Awaiting payment</div>
            <ul className="divide-y divide-zinc-200 border border-zinc-200">
              {issuedInvoices.map((inv) => (
                <li key={inv.id} className="p-4 flex items-center justify-between gap-4" data-testid={`portal-invoice-${inv.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-zinc-500">{inv.invoice_number}</div>
                    <div className="text-sm text-zinc-800">{inv.description}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Issued {new Date(inv.issued_at || inv.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-serif text-2xl text-zinc-900">{formatZAR(inv.amount ?? inv.total)}</span>
                    <button
                      onClick={() => pay(inv.id)}
                      disabled={payingId === inv.id}
                      className="bg-[#0055FF] hover:bg-[#003ECC] text-white text-sm px-4 py-2 disabled:opacity-50"
                      data-testid={`portal-pay-${inv.id}`}
                    >
                      {payingId === inv.id ? "Processing…" : "Pay now"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-zinc-500">Note: this is a stubbed payment flow — clicking "Pay now" simulates a successful payment. A live gateway will be wired in a future release.</p>
          </div>
        )}

        {paidInvoices.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Paid</div>
            <ul className="divide-y divide-zinc-200 border border-zinc-200">
              {paidInvoices.map((inv) => (
                <li key={inv.id} className="p-4 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-zinc-500">{inv.invoice_number}</div>
                    <div className="text-sm text-zinc-800">{inv.description}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Paid {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : "—"}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-serif text-2xl text-zinc-500 line-through">{formatZAR(inv.amount ?? inv.total)}</span>
                    <span className="text-[10px] tracking-widest uppercase px-2 py-1 bg-green-100 text-green-800">paid</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
